import React, { Fragment, useState } from 'react';
import styled from '@emotion/styled';

import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded'
import LocalMallRoundedIcon from '@mui/icons-material/LocalMallRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import TodayRoundedIcon from '@mui/icons-material/TodayRounded';
import NextPlanRoundedIcon from '@mui/icons-material/NextPlanRounded';

import { CheckoutContext, useCheckoutContext } from '../../../contexts/Checkout';
import EditCheckout from '../../Checkout/EditCheckout';

import { BasicFlex, ScrollableContainer } from '../../../utils/Common';

import BasicSideDrawer from '../../../utils/utilities/SideDrawer';

import NoItems from "../../../utils/NoItems";

import { formatGenericDate, formatGenericTime, formatCurrency, relativeTime, formatDayDate, formatPhoneNumber } from "../../../utils/util";

import { CardBlockContainer, CardBlock, CardBlockHeader, CardSpacer } from '../../../utils/utilities/CardComponents';

import MultiStatusChip from '../../../utils/utilities/MultiStatusChip';
import SimpleChip from '../../../utils/utilities/SimpleChip';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import FullDialog from '../../../utils/utilities/Dialog';

import { Tooltip, IconButton, Badge, useTheme, alpha, Button, Card, CardHeader, CardContent, CardActions, Divider, lighten, darken, Tabs, Tab, Paper } from '@mui/material';

import { Formik, Form } from 'formik';

import { string, object, date, boolean } from 'yup';

import { FormDataContext } from '../../../contexts/FormData';

import Pickup from './Pickup';
import { DateTime } from 'luxon';

import { schedulePickup, getScheduledPickups, deleteServiceTicket } from '../../Service_Tickets/apiService';

import useSimpleTable from "../../../hooks/useSimpleTable";

import SearchInput from '../../../utils/utilities/SearchInput';

import useSnackbarStore from '../../../store/snackbar';

const NoItemsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 50%;
  border: 2px dashed ${props => props.theme.colors.border};
  padding: .6rem;
  border-radius: 8px;
  margin: 1rem 0;
` 

const SearchContainer = styled.div`
  max-height: 5rem;
  margin: 1.2rem 0;
` 

const PickupList = styled(ScrollableContainer)`
  border: none;
  border-radius: 8px;
  padding: .8rem .5rem;
  background-color: ${props => props.theme.colors.lightBg};
  min-height: 85vh;
  max-height: 85vh;
  /* margin-top: 1rem; */
`

const PickupCard = styled(Card)`
  border-left-width: 4px;
  border-left-color: ${props => props.$type !== 'service' ? props.theme.colors.primary : props.theme.colors.secondary};
`

const CustName = styled.div`
  font-size: 1.6rem;
  font-weight: 600;
`

const CustNumber = styled.div`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.mildText};
`

const ServiceChip = styled.div`
  font-size: 1.5rem;
  background-color: ${props => lighten(props.theme.colors.secondary, .87)};
  padding: .3rem .8rem;
  border-radius: 5px;
  color: ${props => darken(props.theme.colors.secondary, .4)};
  font-weight: 500;
`

const WigPill = styled.div`
  background-color: ${props => props.theme.colors.softBg};
  padding: .2rem .5rem;
  border-radius: 5px;
  font-size: 1.2rem;
  color: ${props => props.theme.colors.mildText};
  font-family: monospace;
`

const CardDateTime = styled.div`
  color: ${props => props.theme.colors.mildText};
  font-size: 1.2rem;
  /* font-family: monospace; */
  /* font-weight: 600; */
`

const DeleteWarning = styled.div`
  margin: 0 1.6rem;
  font-size: 1.6rem;
`

const EMPTY_FORM_VALUES = {
  inventory_id: "",
  service_price: 0,
  total: 0,
  client_name: "",
};

const getInitialValues = () => ({
  customer_id: "",
  service_name: "",
  stylist_id: "",
  wig_id: "",
  is_checked_in: true,
  scheduled_pickup_date: DateTime.now()
});

const PickupItem = ({ pickup, setExpand }) => {
  const { service, setService } = useCheckoutContext();

  const serviceCheckout = () => {
    setService(pickup)
    setExpand(false)
  }

   const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
    const queryClient = useQueryClient();

    const { mutate } = useMutation({
      mutationFn: () => deleteServiceTicket(pickup?.service_ticket_id),
      onSuccess: () => {
        queryClient.invalidateQueries(["pickups"]);
        showSnackbar("Successfully deleted!", "success");
      },
      onError: () => {
        showSnackbar("Something went wrong!", "error");
      },
    });

  const { client_name, customer_phone, status, service_name, wig_id, pickup_type = 'service' } = pickup
  return (
    <PickupCard variant="outlined" sx={{pt: 1, pb: 1, width: "100%"}} $type={pickup_type}>
      <BasicFlex px="1.6rem" py=".5rem" justify="space-between">
        <BasicFlex dir="column" gap=".1rem">
          <CustName>{client_name}</CustName>
          <CustNumber>{formatPhoneNumber(customer_phone)}</CustNumber>
        </BasicFlex>
        <div style={{minWidth: "max-content"}}>
          <MultiStatusChip value={status} values={[
            { label: "Awaiting Dropoff", value: "scheduled", status: "primary" },
            { label: "Prepping", value: "prepping", status: "warning" },
            { label: "In Progress", value: "in_progress", status: "tertiary" },
            { label: "Prep Done", value: "prep_done", status: "info" },
            { label: "Checked In", value: "checked_in", status: "secondary" },
            { label: "Completed", value: "completed", status: "success" },
          ]} />
        </div>
      </BasicFlex>

      <CardContent sx={{p: 2, pt: 0, pb: "0 !important"}}>
        <BasicFlex gap="1rem" justify="flex-start" py="1rem">
         <ServiceChip>{service_name}</ServiceChip>
          <WigPill>{wig_id}</WigPill>
        </BasicFlex>
      </CardContent>
      <BasicFlex px="1.6rem" py=".5rem" gap=".6rem">
        <BasicFlex justify="flex-start" gap=".5rem">
          <CalendarMonthRoundedIcon fontSize="small" color="icon" />
        <CardDateTime>{formatDayDate(pickup.scheduled_pickup_date)} &middot; {relativeTime(pickup.scheduled_pickup_date)}</CardDateTime>
        </BasicFlex>
        <Button sx={{minWidth: "max-content"}} variant="outlined" size="small" onClick={serviceCheckout} color="secondary">Complete Checkout</Button>
        <FullDialog
            title="Confirm Delete?"
            toggle={<Tooltip title="Delete pickup">
            <Button color="error" size="small" sx={{minWidth: "max-content", padding: ".4rem .2rem"}} variant="outlined">
              <DeleteRoundedIcon fontSize="small" />
            </Button>
            </Tooltip>}
            saveFunc={() => mutate()}
          >
           <DeleteWarning>Are you sure you want to delete this pickup? This action cannot be undone. Any attached invoices will be deleted as well.</DeleteWarning>
          </FullDialog>
      </BasicFlex>
    </PickupCard>
  )
}

const SideDrawer = ({ expand, setExpand, pickups }) => {

  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

  const [period, setPeriod] = useState("today"); // today or upcoming

  // Upcoming pickups: same endpoint, starting from tomorrow
  const { data: upcomingData } = useQuery({
    queryKey: ['pickups', 'upcoming'],
    queryFn: () => getScheduledPickups(DateTime.now().plus({ days: 1 }).toISODate()),
    enabled: expand && period === "upcoming"
  })

  const activePickups = period === "today" ? pickups : (upcomingData?.rows || []);

  const { data: pickup_list, pagination, setPagination, setSearchTerm } = useSimpleTable({localData: activePickups, rowsPerPage: 200, searchFields: ["client_name", "customer_phone"], defaultSort: "scheduled_pickup_date"});

  const { service, setService } = useCheckoutContext();

  const [formValues, setFormValues] = useState(EMPTY_FORM_VALUES);

  const [addPickup, setAddPickup] = useState(false)

  const queryClient = useQueryClient();

  const schema = object().shape({
    customer_id: string(),
    service_name: string(),
    stylist_id: string(),
    wig_id: string(),
    is_checked_in: boolean(),
    scheduled_pickup_date: date()
  })

  const { mutate } = useMutation({
    mutationFn: schedulePickup,
    onSuccess: () => {
      queryClient.invalidateQueries(['pickups'])
      showSnackbar('Scheduled successfully!', 'success');
    },
    onError: () => {
      showSnackbar('Something went wrong!', 'error');
    }
  })

  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    let dataPacket = {
      ...values, ...formValues
    }
    mutate({dataPacket}, {
      onSuccess: () => {
        resetForm({ values: getInitialValues() });
        setFormValues(EMPTY_FORM_VALUES);
      },
      onSettled: () => setSubmitting(false)
    });
  }

  return (
    <BasicSideDrawer open={expand} title="Ready For Pickup" handleClose={() => {
      setExpand(false)
      setService(null)
    }}>

    <FormDataContext.Provider value={{formValues, setFormValues}}>
      <Formik
        initialValues={getInitialValues()}
        // enableReinitialize
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {() => (
          <Form>
            <Pickup />
          </Form>
        )}
      </Formik>
    </FormDataContext.Provider>

    <Paper elevation={2} sx={{mt: "1rem", pb: "0"}} square>
      <Tabs value={period} onChange={(e, newValue) => setPeriod(newValue)} sx={{pb: "0"}} variant="fullWidth">
        <Tab label="Pickup Today" value="today" icon={<TodayRoundedIcon />} iconPosition="start" sx={{pb: "0", minHeight: "auto"}} />
        <Tab label="Scheduled Pickups" value="upcoming" icon={<NextPlanRoundedIcon />} iconPosition="start" sx={{pb: "0", minHeight: "auto"}} />
      </Tabs>
    </Paper>

    <SearchContainer>
      <SearchInput onChange={(e) => setSearchTerm(e.target.value)} fieldsToSearch={["Customer", "Phone"]} />
    </SearchContainer>

      {pickup_list?.rows?.length ? (
        <PickupList>
          <BasicFlex dir="column" gap="1rem">
            {pickup_list?.rows?.map(pickup => <PickupItem key={pickup.service_ticket_id} pickup={pickup} setExpand={setExpand} />)}
          </BasicFlex>
        </PickupList>) : <NoItemsContainer><NoItems title={period === "today" ? "No items ready for pickup" : "No scheduled pickups"} /></NoItemsContainer>}

    </BasicSideDrawer>
  )
}

const ScheduledPickup = () => {
  const [expand, setExpand] = useState(false);
  const [service, setService] = useState(null);
  const theme = useTheme();

  const { data } = useQuery({
    queryKey: ['pickups', 'ready'],
    queryFn: () => getScheduledPickups(),
    // enabled: expand
  })

  let pickups = data?.rows || [];
  
  return (
    <CheckoutContext.Provider value={{ service, setService }} >
      <Tooltip title="Ready For Pickup">
        <IconButton onClick={() => setExpand(true)} color="success" sx={{backgroundColor: alpha(theme.palette.success.main, 0.2)}}>
          <Badge color="success" badgeContent={+data?.count} showZero={false} >
            <LocalMallRoundedIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <SideDrawer expand={expand} setExpand={setExpand} pickups={pickups} />
      <FullDialog fullScreen forceOpen={service?.service_ticket_id ? true : false} title="Service Checkout" useForm={true} 
        forceClose={() => setService(null)} 
        >
        <EditCheckout invalidateQueries={[['pickups', 'ready'], ['pickups', 'upcoming']]} />
      </FullDialog>
    </CheckoutContext.Provider>
  )
};

export default ScheduledPickup;